import React, { useCallback, useEffect, useState } from 'react';

import { KID_ICON_PATHS } from '../../../../design-system/kid-nav';
import {
  LearnerProfile as Profile,
  getLearnerProfile,
  updateLearnerProfile,
} from '../../api/learner-explore';
import { ACCENT_COLOURS } from '../../learner/requestWords';

type Load = { status: 'loading' } | { status: 'error' } | { status: 'ready'; profile: Profile };

const ACCENT_WORDS: Record<string, string> = {
  orange: 'Naranja',
  blue: 'Azul',
  green: 'Verde',
  violet: 'Violeta',
  red: 'Rojo',
};

const AVATAR_WORDS: Record<string, string> = {
  frog: 'Rana',
  rocket: 'Cohete',
  puzzle: 'Rompecabezas',
  crown: 'Corona',
  sparkle: 'Destello',
  star: 'Estrella',
};

const Avatar: React.FC<{ name: string | null; colour?: string; size?: number }> = ({
  name,
  colour,
  size = 40,
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={colour ?? 'currentColor'}
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={size}
    height={size}
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: KID_ICON_PATHS[name ?? 'person'] ?? KID_ICON_PATHS.person }}
  />
);

/**
 * UC-S02 — the learner's own profile, read-mostly (DEC-S2-S02-01, S02-02).
 *
 * Avatar, interests and colour are theirs to change. The name and grade are
 * shown and belong to the family: they have no control here, and the server has
 * no route that would take them. The lists are the server's. A save that fails
 * says so and keeps what is on screen in step with what was stored.
 */
const LearnerProfile: React.FC = () => {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const fetchProfile = useCallback(() => {
    setLoad({ status: 'loading' });
    getLearnerProfile()
      .then((profile) => setLoad({ status: 'ready', profile }))
      .catch(() => setLoad({ status: 'error' }));
  }, []);

  useEffect(fetchProfile, [fetchProfile]);

  const save = async (patch: Parameters<typeof updateLearnerProfile>[0]) => {
    setSaving(true);
    setFailed(false);
    try {
      setLoad({ status: 'ready', profile: await updateLearnerProfile(patch) });
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className="hi">Mi perfil</h1>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tu perfil…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar tu perfil</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchProfile}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' &&
        (() => {
          const { profile } = load;
          const colour = ACCENT_COLOURS[profile.visualPreferences.accent ?? ''];
          return (
            <>
              <div className="lrn-status">
                <Avatar name={profile.avatar} colour={colour} size={56} />
                <h2>{profile.firstName}</h2>
                <p>Grado: {profile.grade}</p>
                <p>Tu nombre y tu grado los cambia un adulto de tu familia.</p>
              </div>

              <div className="lrn-status">
                <h2>Mi avatar</h2>
                <div className="lrn-picks">
                  {profile.options.avatars.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="lrn-pick"
                      aria-label={AVATAR_WORDS[name] ?? name}
                      aria-pressed={profile.avatar === name}
                      disabled={saving}
                      onClick={() => save({ avatar: name })}
                    >
                      <Avatar name={name} colour={colour} size={28} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="lrn-status">
                <h2>Mi color</h2>
                <div className="lrn-picks">
                  {profile.options.accents.map((accent) => (
                    <button
                      key={accent}
                      type="button"
                      className="lrn-pick"
                      aria-pressed={profile.visualPreferences.accent === accent}
                      disabled={saving}
                      onClick={() => save({ visualPreferences: { accent } })}
                    >
                      {ACCENT_WORDS[accent] ?? accent}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lrn-status">
                <h2>Lo que me interesa</h2>
                <div className="lrn-picks">
                  {profile.options.interests.map((interest) => {
                    const on = profile.interests.includes(interest);
                    return (
                      <button
                        key={interest}
                        type="button"
                        className="lrn-pick"
                        aria-pressed={on}
                        disabled={saving}
                        onClick={() =>
                          save({
                            interests: on
                              ? profile.interests.filter((i) => i !== interest)
                              : [...profile.interests, interest],
                          })
                        }
                      >
                        {interest}
                      </button>
                    );
                  })}
                </div>
              </div>

              {failed && (
                <p className="hi-s" role="alert">
                  No pudimos guardar el cambio. Prueba otra vez.
                </p>
              )}
            </>
          );
        })()}
    </>
  );
};

export default LearnerProfile;
